import { Plus, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import microsoftFundamentals from '../../../assets/images/microsoft-fundamentals.svg';
import { setOpenCard } from '../../../redux/Slices/CloudPrepSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/store/hooks';

const SET_OPEN_CARD = 'SET_OPEN_CARD';

function MicrosoftCard() {
  const dispatch = useAppDispatch();

  const { openCard } = useAppSelector(state => state.root.cloudPrep);

  const handleToggle = (card: string) => {
    dispatch(setOpenCard(openCard === card ? null : card));
  };

  const navigate = useNavigate();
  const handleClickCloudPractitioner = () => {
    dispatch(setOpenCard('AZURE_DP_900'));
    navigate(`/cloud-prep-tabs/${'MICROSOFT_AZURE'}/${'AZURE_DP_900'}`);
  };

  const handleClickAIPractitioner = () => {
    dispatch(setOpenCard('AZURE_AI_900'));
    navigate(`/cloud-prep-tabs/${'MICROSOFT_AZURE'}/${'AZURE_AI_900'}`);
  };

  return (
    <div className='flex flex-col items-center justify-center gap-8 p-6 md:flex-row'>
      {/* ---------- CARD 1 ---------- */}
      <div className='group relative h-[440px] w-80 cursor-pointer'>
        <div className='absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-400 via-pink-500 via-purple-500 to-blue-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-75'></div>
        <div className='relative h-full w-full rounded-2xl bg-white shadow-md'>
          {openCard !== 'cloud' ? (
            <div className='flex flex-col'>
              {/* Image wrapper */}
              <div
                onClick={handleClickCloudPractitioner}
                className='overflow-hidden rounded-lg pt-8'
              >
                <img
                  src={microsoftFundamentals}
                  alt='AWS Certified Cloud Practitioner'
                  className='h-[150px]  w-full object-contain transition-transform duration-300 group-hover:scale-110'
                />
              </div>

              <h2
                onClick={handleClickCloudPractitioner}
                className='mt-4 px-6 text-lg font-semibold text-gray-900'
              >
                Microsoft Certified: Azure Fundamentals
              </h2>
              <p
                onClick={handleClickCloudPractitioner}
                className='mt-2 px-6 text-sm text-gray-600'
              >
                Demonstrate foundational knowledge of cloud concepts, core Azure
                services, plus Azure management and governance features and
                tools.
              </p>
              <div className='mt-auto flex justify-between'>
                {/* Learn More Button (arrow always visible, text slides in) */}
                <div
                  onClick={handleClickCloudPractitioner}
                  className='group relative mt-7 flex w-full items-center justify-start overflow-hidden p-6'
                >
                  {/* Text slides in */}
                  <span className='absolute left-6 -translate-x-4 transform text-sm font-medium text-gray-800 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100'>
                    Learn more
                  </span>

                  {/* Arrow starts at beginning and moves slightly on hover */}
                  <ArrowRight className='mt-[4px] h-4 w-4 text-gray-800 transition-transform duration-500 ease-in-out group-hover:translate-x-20' />
                </div>
                <div>
                  {/* Toggle Button */}
                  {openCard !== 'cloud' && (
                    <button
                      onClick={() => handleToggle('cloud')}
                      className='absolute bottom-4 right-4 rounded-full bg-primary p-2 transition hover:bg-blue_a12'
                    >
                      <Plus className='h-5 w-5 text-white' />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='flex h-full flex-col p-6 text-left'>
              <div className='mb-3 flex items-center justify-between '>
                <h3 className='text-lg font-semibold text-green-700'>
                  About this exam
                </h3>

                <button
                  onClick={() => handleToggle('cloud')}
                  className='rounded-full bg-gray-100 p-2 transition hover:bg-gray-200'
                >
                  {openCard === 'cloud' && (
                    <X className='h-5 w-5 text-gray-800' />
                  )}
                </button>
              </div>
              <p className='mb-6 text-sm leading-relaxed text-gray-700'>
                Microsoft Certified: Azure Fundamentals validates foundational
                knowledge of Microsoft Azure, its core services, and cloud
                concepts. It’s ideal for individuals seeking to understand
                Azure’s capabilities, pricing, and cloud solutions without
                requiring technical experience.
              </p>
              <div className='mt-auto'>
                <button className='group mt-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-medium text-white transition'>
                  Learn more
                  <ArrowRight className='h-4 w-4 transform transition-transform duration-300' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- CARD 2 ---------- */}
      <div className='group relative h-[440px] w-80 cursor-pointer'>
        {/* Gradient Glow */}
        <div className='absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-400 via-pink-500 via-purple-500 to-blue-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-75'></div>

        {/* White Card */}
        <div className='relative h-full w-full rounded-2xl bg-white shadow-md'>
          {openCard !== 'ai' ? (
            <div className='z-10 flex h-full flex-col'>
              <div
                onClick={handleClickAIPractitioner}
                className='overflow-hidden rounded-t-2xl pt-8'
              >
                <img
                  src={microsoftFundamentals}
                  alt='AWS Certified AI Practitioner'
                  className='h-[150px] w-full object-contain transition-transform duration-300 group-hover:scale-110'
                />
              </div>

              <h2
                onClick={handleClickAIPractitioner}
                className='mt-4 px-6 text-lg font-semibold text-gray-900'
              >
                Microsoft Certified: Azure AI Fundamentals
              </h2>
              <p
                onClick={handleClickAIPractitioner}
                className='mt-2 px-6 text-sm text-gray-600'
              >
                Demonstrate fundamental AI concepts related to the development
                of software and services of Microsoft Azure to create AI
                solutions.
              </p>
              <div className='mt-auto flex justify-between'>
                {/* Learn More Button */}
                <div
                  onClick={handleClickAIPractitioner}
                  className='group relative mt-5 flex w-full items-center justify-start overflow-hidden p-6'
                >
                  <span className='absolute left-6 -translate-x-4 transform text-sm font-medium text-gray-800 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100'>
                    Learn more .
                  </span>
                  <ArrowRight className='mt-[4px] h-4 w-4 text-gray-800 transition-transform duration-500 ease-in-out group-hover:translate-x-20' />
                </div>
                <div>
                  {/* Toggle Button */}
                  {openCard !== 'ai' && (
                    <button
                      onClick={() => handleToggle('ai')}
                      className='absolute bottom-4 right-4 rounded-full bg-primary p-2 transition hover:bg-blue_a12'
                    >
                      <Plus className='h-5 w-5 text-white' />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='flex h-full flex-col p-6 text-left'>
              <div className='mb-3 flex items-center justify-between'>
                <h3 className='text-lg font-semibold text-green-700'>
                  About this exam
                </h3>
                <button
                  onClick={() => handleToggle('ai')}
                  className='rounded-full bg-gray-100 p-2 transition hover:bg-gray-200'
                >
                  <X className='h-5 w-5 text-gray-800' />
                </button>
              </div>
              <p className='mb-6 text-sm leading-relaxed text-gray-700'>
                Microsoft Certified: Azure AI Fundamentals validates
                foundational knowledge of artificial intelligence (AI) and
                machine learning (ML) concepts on the Microsoft Azure platform.
                It’s ideal for individuals seeking to understand AI/ML services,
                their use cases, and implementation basics without requiring
                technical expertise.
              </p>
              <div className='mt-auto'>
                <button className='group mt-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-medium text-white transition'>
                  Learn more
                  <ArrowRight className='h-4 w-4 transform transition-transform duration-300' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MicrosoftCard;
